import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Menu, Settings, LogOut, Sun, Moon, Tv } from 'lucide-react';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { nip19 } from 'nostr-tools';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useTheme } from '@/hooks/useTheme';
import { useLoginActions } from '@/hooks/useLoginActions';

interface MobileTopBarProps {
  onOpenCompose: () => void;
}

export function MobileTopBar({ onOpenCompose }: MobileTopBarProps) {
  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey ?? '');
  const metadata = author.data?.metadata;
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { logout } = useLoginActions();

  const userNpub = user ? nip19.npubEncode(user.pubkey) : '';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="flex md:hidden items-center justify-between px-4 h-14 border-b border-border sticky top-0 bg-background/95 backdrop-blur-md z-40">
      {/* Drawer trigger (User Avatar or menu icon) */}
      <Sheet>
        <SheetTrigger asChild>
          <button className="flex items-center justify-center rounded-full cursor-pointer focus-visible:outline-hidden">
            {user ? (
              <img 
                src={metadata?.picture || `https://robohash.org/${user.pubkey}.png?set=set4`}
                alt={metadata?.name || 'User'} 
                className="size-8 rounded-full object-cover border border-border"
              />
            ) : (
              <div className="p-2 hover:bg-secondary rounded-full">
                <Menu className="size-5" />
              </div>
            )}
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[280px] p-5 flex flex-col justify-between rounded-r-2xl border-r border-border">
          <div className="flex flex-col gap-6">
            <SheetHeader className="text-left flex flex-col gap-1 items-start">
              <BrandLogo className="size-6 text-foreground mb-4" />
              {user ? (
                <div className="flex flex-col gap-2">
                  <img 
                    src={metadata?.picture || `https://robohash.org/${user.pubkey}.png?set=set4`}
                    alt={metadata?.name || 'User'} 
                    className="size-14 rounded-full object-cover border border-border"
                  />
                  <div className="flex flex-col leading-tight mt-1">
                    <span className="font-extrabold text-lg">{metadata?.display_name || metadata?.name || 'Anonymous'}</span>
                    <span className="text-sm text-muted-foreground">@{userNpub.slice(0, 10)}...</span>
                  </div>
                  {metadata?.about && (
                    <p className="text-sm text-foreground/80 mt-2 line-clamp-3 leading-normal">{metadata.about}</p>
                  )}
                </div>
              ) : (
                <SheetTitle className="font-bold text-lg">Menu</SheetTitle>
              )}
            </SheetHeader>

            {/* Menu Links */}
            <nav className="flex flex-col gap-2">
              <NavLink 
                to="/" 
                className="flex items-center gap-4 py-3 px-2 rounded-xl text-base font-bold hover:bg-secondary/40 transition-colors"
              >
                <span>Home</span>
              </NavLink>
              <NavLink 
                to="/explore" 
                className="flex items-center gap-4 py-3 px-2 rounded-xl text-base font-bold hover:bg-secondary/40 transition-colors"
              >
                <span>Explore</span>
              </NavLink>
              {user && (
                <>
                  <NavLink 
                    to="/chat" 
                    className="flex items-center gap-4 py-3 px-2 rounded-xl text-base font-bold hover:bg-secondary/40 transition-colors"
                  >
                    <span>Messages</span>
                  </NavLink>
                  <NavLink 
                    to="/notifications" 
                    className="flex items-center gap-4 py-3 px-2 rounded-xl text-base font-bold hover:bg-secondary/40 transition-colors"
                  >
                    <span>Notifications</span>
                  </NavLink>
                  <NavLink 
                    to="/bookmarks" 
                    className="flex items-center gap-4 py-3 px-2 rounded-xl text-base font-bold hover:bg-secondary/40 transition-colors"
                  >
                    <span>Bookmarks</span>
                  </NavLink>
                  <NavLink 
                    to={`/${userNpub}`} 
                    className="flex items-center gap-4 py-3 px-2 rounded-xl text-base font-bold hover:bg-secondary/40 transition-colors"
                  >
                    <span>Profile</span>
                  </NavLink>
                </>
              )}
              <NavLink 
                to="/settings" 
                className="flex items-center gap-4 py-3 px-2 rounded-xl text-base font-bold hover:bg-secondary/40 transition-colors"
              >
                <span>Settings</span>
              </NavLink>
            </nav>
          </div>

          {/* Drawer Footer (Theme & Logout) */}
          <div className="flex flex-col gap-4 border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-muted-foreground">Appearance</span>
              <div className="flex items-center gap-1 bg-secondary/40 p-1 rounded-full border border-border">
                <button 
                  onClick={() => setTheme('light')} 
                  className={`p-1.5 rounded-full ${theme === 'light' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground'}`}
                >
                  <Sun className="size-3.5" />
                </button>
                <button 
                  onClick={() => setTheme('dim')} 
                  className={`p-1.5 rounded-full ${theme === 'dim' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground'}`}
                >
                  <Tv className="size-3.5" />
                </button>
                <button 
                  onClick={() => setTheme('dark')} 
                  className={`p-1.5 rounded-full ${theme === 'dark' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground'}`}
                >
                  <Moon className="size-3.5" />
                </button>
              </div>
            </div>

            {user && (
              <button 
                onClick={handleLogout}
                className="flex items-center gap-3 py-2 w-full text-red-500 hover:text-red-400 font-bold transition-colors cursor-pointer"
              >
                <LogOut className="size-5" />
                <span>Log out</span>
              </button>
            )}

            <div className="text-[10px] text-muted-foreground">
              <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="hover:underline text-sky-500 font-semibold">
                Vibed with Shakespeare
              </a>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Center Logo */}
      <NavLink to="/" className="flex items-center justify-center p-2 rounded-full hover:bg-secondary/45 transition-colors">
        <BrandLogo className="size-5 text-foreground" />
      </NavLink>

      {/* Quick Search */}
      <button 
        onClick={() => navigate('/explore')} 
        className="p-2 hover:bg-secondary rounded-full cursor-pointer"
      >
        <Settings className="size-5 text-foreground" />
      </button>
    </div>
  );
}
